import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { ADMIN_ORGANIZATION_ID } from '../config/api';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Grid,
  IconButton,
  Typography,
  Alert,
  CircularProgress,
  TextField,
  Chip,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
  Save as SaveIcon,
  Undo as UndoIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import {
  WalteroCard,
  WalteroButton,
  WalteroStatusChip,
  WalteroTypography,
  WalteroChart
} from './waltero';
import { useTheme } from '@mui/material/styles';
import telemetryService from '../services/telemetryService';
import { getUserTimezone, formatDateInTimezone } from '../utils/timezoneUtils';
import { hasOrganizationAccess } from '../utils/auth';

/**
 * DataCorrectionModal - Admin-only modal for correcting timestream sensor data
 * Features: ECharts brush selection, digit-level value modification, bulk updates
 */
const DataCorrectionModal = ({ sensor, organizationId, startDate, endDate, onClose, onDataUpdated }) => {
  const theme = useTheme();
  
  // Get user's timezone for consistent display
  const userTimezone = getUserTimezone();
  console.log('🌍 User timezone:', userTimezone);
  
  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sensorData, setSensorData] = useState(null);
  const [selectedPoints, setSelectedPoints] = useState([]);
  const [selectedDigitPosition, setSelectedDigitPosition] = useState(null);
  const [newDigitValue, setNewDigitValue] = useState('');
  const [previewChanges, setPreviewChanges] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [updateError, setUpdateError] = useState(null);
  const [updateSuccess, setUpdateSuccess] = useState(null);
  
  // Additional digit positions (expanded beyond original data)
  const [additionalLeftDigits, setAdditionalLeftDigits] = useState(0); // Higher significance digits
  const [additionalRightDigits, setAdditionalRightDigits] = useState(0); // Lower significance digits
  
  // Manual value entry mode
  const [isManualEntryMode, setIsManualEntryMode] = useState(false);
  const [manualValue, setManualValue] = useState('');
  
  // Track if any data was updated during this session
  const [dataWasUpdatedThisSession, setDataWasUpdatedThisSession] = useState(false);
  
  // Filter visualization state
  const [showFilteredData, setShowFilteredData] = useState(false);
  const [filteredData, setFilteredData] = useState(null);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState(null);
  
  // Drag-and-drop filter system state
  const [availableFilters, setAvailableFilters] = useState([
    {
      id: 'digitConfusion',
      name: 'Digit Confusion Filter',
      description: 'Corrects misread digits based on confidence scores',
      params: {
        confidenceThreshold: 0.7,
        windowSize: 7,
        statisticalThreshold: 2.0,
        positionWeighting: true
      }
    },
    {
      id: 'digitCompletion',
      name: 'Digit Completion Filter',
      description: 'Completes missing digits for failed reads (volume = -1) using surrounding data patterns',
      params: {
        confidenceThreshold: 0.8,
        lookAheadWindow: 10,
        patternAnalysisDepth: 5,
        useForwardTrend: true
      }
    }
  ]);
  const [selectedFilterChain, setSelectedFilterChain] = useState([]);
  const [showFilterInterface, setShowFilterInterface] = useState(false);
  const [draggedFilter, setDraggedFilter] = useState(null);
  
  // Check admin access for filter functionality
  const ADMIN_ORG_ID = ADMIN_ORGANIZATION_ID;
  const isAdmin = hasOrganizationAccess(ADMIN_ORG_ID);
  
  // Use ref to get current selectedPoints state in callbacks
  const selectedPointsRef = useRef(selectedPoints);
  selectedPointsRef.current = selectedPoints;

  // Load sensor data on mount
  useEffect(() => {
    loadSensorData();
  }, [sensor.deviceId, organizationId, startDate, endDate]);

  // Debug: Log when selectedPoints changes
  useEffect(() => {
    console.log('🔄 selectedPoints state changed:', {
      count: selectedPoints.length,
      points: selectedPoints.slice(0, 3).map(p => ({ 
        value: p.value, 
        time: p.displayDateTime || `${p.date} ${p.time}` 
      }))
    });
  }, [selectedPoints]);

  // Sort selected points by timestamp for consistent display
  const sortedSelectedPoints = useMemo(() => {
    return [...selectedPoints].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
  }, [selectedPoints]);

  const loadSensorData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('🔄 Loading sensor data for correction:', sensor.serialNumber);
      console.log('📅 Date range:', { startDate, endDate });
      
      const data = await telemetryService.getSensorAllData(sensor.deviceId, organizationId, startDate, endDate);
      
      console.log('📊 Raw API response:', data);
      console.log('📊 Chart data structure:', data.chartData);
      
      if (!data.chartData || !data.chartData.volume || !data.chartData.volume[sensor.deviceId]) {
        throw new Error('No volume data available for this sensor in the selected time range');
      }
      
      setSensorData(data);
      const volumeDataPoints = data.chartData.volume[sensor.deviceId]?.length || 0;
      console.log('✅ Sensor data loaded:');
      console.log('  - Total records in metadata:', data.metadata?.totalRecords || 0);
      console.log('  - Volume data points:', volumeDataPoints);
      console.log('  - Raw data points sample:', data.chartData.volume[sensor.deviceId]?.slice(0, 5));
    } catch (err) {
      console.error('❌ Failed to load sensor data:', err);
      setError(err.message || 'Failed to load sensor data');
    } finally {
      setLoading(false);
    }
  };

  // Transform data for chart display
  const chartData = useMemo(() => {
    if (!sensorData?.chartData?.volume?.[sensor.deviceId]) return [];
    
    const volumeData = sensorData.chartData.volume[sensor.deviceId];
    console.log('🔍 Raw volume data sample:', volumeData.slice(0, 3));
    
    // Convert to [{timestamp, value, date}] format
    // Handle both old format [timestamp, value] and new format [timestamp, value, metadata]
    const transformedData = volumeData.map((item, index) => {
      const [timestamp, value, metadata] = item;
      
      // Use measurement time from metadata for chart display
      // metadata.measurementTime is the actual measurement time from 'time' field
      // timestamp (first element) is currently the transmission time from 'timestamp' field
      const chartTimestamp = metadata?.measurementTime || new Date(timestamp).toISOString();
      
      // Format timestamps in user's timezone for display
      const userTimezoneDate = formatDateInTimezone(chartTimestamp, userTimezone, 'MM/dd/yyyy');
      const userTimezoneTime = formatDateInTimezone(chartTimestamp, userTimezone, 'HH:mm:ss');
      const userTimezoneDateTime = formatDateInTimezone(chartTimestamp, userTimezone, 'yyyy-MM-dd HH:mm:ss');
      
      return {
        timestamp: chartTimestamp, // Keep original UTC timestamp for chart X-axis and backend
        value: value,
        date: userTimezoneDate, // Display date in user's timezone
        time: userTimezoneTime, // Display time in user's timezone  
        displayDateTime: userTimezoneDateTime, // Full datetime in user's timezone for tooltips
        measurementTime: metadata?.measurementTime || new Date(timestamp).toISOString(),
        transmissionTime: metadata?.transmissionTime || new Date(timestamp).toISOString(),
        recordIndex: metadata?.recordIndex || index,
        originalRecord: metadata?.originalRecord
      };
    }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    console.log('🔍 Transformed chart data sample:', transformedData.slice(0, 3));
    console.log('🔍 Total data points:', transformedData.length);
    
    return transformedData;
  }, [sensorData, sensor.deviceId]);

  // Generate chart configuration with brush selection
  const chartOption = useMemo(() => {
    if (!chartData.length) return {};

    console.log('🔄 Chart option updated with', selectedPoints.length, 'selected points and', previewChanges.length, 'preview changes');
    
    // Calculate data range for Y-axis padding including preview changes and filtered data (admin only)
    const originalValues = chartData.map(point => point.value);
    const previewValues = previewChanges.map(change => change.newValue);
    const filteredValues = (isAdmin && showFilteredData && filteredData?.chartData) ? filteredData.chartData.map(point => point.value) : [];
    const allValues = [...originalValues, ...previewValues, ...filteredValues];
    
    const minValue = Math.min(...allValues);
    const maxValue = Math.max(...allValues);
    const valueRange = maxValue - minValue;
    const paddingPercentage = 0.1; // 10% padding
    const padding = Math.max(valueRange * paddingPercentage, valueRange > 0 ? valueRange * 0.05 : 1); // Minimum 5% or 1 unit
    
    const yAxisMin = minValue - padding;
    const yAxisMax = maxValue + padding;
    
    console.log('📊 Y-axis range calculation:', {
      originalCount: originalValues.length,
      previewCount: previewValues.length,
      originalMin: Math.min(...originalValues).toFixed(3),
      originalMax: Math.max(...originalValues).toFixed(3),
      previewMin: previewValues.length > 0 ? Math.min(...previewValues).toFixed(3) : 'none',
      previewMax: previewValues.length > 0 ? Math.max(...previewValues).toFixed(3) : 'none',
      finalMin: minValue.toFixed(3),
      finalMax: maxValue.toFixed(3),
      valueRange: valueRange.toFixed(3),
      padding: padding.toFixed(3),
      yAxisMin: yAxisMin.toFixed(3),
      yAxisMax: yAxisMax.toFixed(3)
    });

    return {
      title: {
        text: `Volume Data - ${sensor.serialNumber}${isAdmin && showFilteredData ? ' (with Filter Results)' : ''}`,
        subtext: `${chartData.length} raw readings${isAdmin && filteredData?.filtered ? ` • ${filteredData.filtered.corrections} corrections${filteredData.chainResults ? ` from ${filteredData.chainResults.length} filters` : ''}` : ''} • Click points to select • Drag rectangles to add ranges • Times in ${userTimezone}`,
        left: 'center',
        top: 20
      },
      
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross',
          animation: false
        },
        formatter: (params) => {
          if (!params.length) return '';
          
          // Handle main series, selected points, preview series, and filtered data
          const param = params[0];
          let point;
          let isSelected = false;
          let isPreview = false;
          let isFiltered = false;
          let isCorrected = false;
          let originalValue = null;
          
          if (param.seriesName === 'Selected Points') {
            // Find the corresponding point in selectedPoints
            const selectedPoint = selectedPoints.find(p => 
              new Date(p.timestamp).getTime() === param.value[0] && p.value === param.value[1]
            );
            point = selectedPoint || chartData[param.dataIndex];
            isSelected = true;
          } else if (param.seriesName === 'Modified Values') {
            // Find the corresponding preview change
            const previewChange = previewChanges.find(p => 
              new Date(p.timestamp).getTime() === param.value[0] && p.newValue === param.value[1]
            );
            point = previewChange || chartData[param.dataIndex];
            isPreview = true;
            originalValue = previewChange?.oldValue;
          } else if (param.seriesName === 'Filtered Data') {
            // Find the corresponding filtered point
            const filteredPoint = filteredData?.chartData?.find(p => 
              new Date(p.timestamp).getTime() === param.value[0] && p.value === param.value[1]
            );
            point = filteredPoint || chartData[param.dataIndex];
            isFiltered = true;
            originalValue = filteredPoint?.originalValue;
          } else if (param.seriesName === 'Filter Corrections') {
            // Find the corresponding corrected point
            const correctedPoint = filteredData?.chartData?.find(p => 
              new Date(p.timestamp).getTime() === param.value[0] && p.value === param.value[1] && p.corrected
            );
            point = correctedPoint || chartData[param.dataIndex];
            isCorrected = true;
            isFiltered = true;
            originalValue = correctedPoint?.originalValue;
          } else {
            // Main data series
            point = chartData[param.dataIndex];
          }
          
          if (!point) return '';
          
          return `
            <strong>${point.displayDateTime}</strong><br/>
            ${isPreview ? 
              `New Value: <strong style="color: ${theme.palette.waltero.status.success};">${point.newValue || point.value}</strong> 💎<br/>
               Original: <strong>${originalValue}</strong><br/>
               Change: <strong>${((point.newValue || point.value) - originalValue).toFixed(2)}</strong>` :
              isCorrected ?
              `Corrected Value: <strong style="color: ${theme.palette.waltero.status.warning};">${point.value}</strong> 🔧<br/>
               Original: <strong>${originalValue}</strong><br/>
               Correction: <strong>${(point.value - originalValue).toFixed(2)}</strong>` :
              isFiltered ?
              `Filtered Value: <strong style="color: ${theme.palette.waltero.primary.mediumBlue};">${point.value}</strong> 🔍<br/>
               ${originalValue !== undefined && originalValue !== point.value ? `Original: <strong>${originalValue}</strong><br/>` : ''}` :
              `Volume: <strong>${point.value}</strong>${isSelected ? ' 🔴' : ''}`
            }<br/>
            UTC Timestamp: ${point.timestamp}<br/>
            <small>Timezone: ${userTimezone}</small>
            ${isSelected ? `<br/><small style="color: ${theme.palette.waltero.status.error};">Selected for modification (click to deselect)</small>` : ''}
            ${isPreview ? `<br/><small style="color: ${theme.palette.waltero.status.success};">Preview of modification</small>` : ''}
            ${isCorrected ? `<br/><small style="color: ${theme.palette.waltero.status.warning};">Filter correction applied</small>` : ''}
            ${isFiltered && !isCorrected ? `<br/><small style="color: ${theme.palette.waltero.primary.mediumBlue};">Filtered data (no correction needed)</small>` : ''}
            ${!isSelected && !isPreview && !isFiltered ? `<br/><small style="color: ${theme.palette.waltero.neutral.mediumGray};">Click to select this point</small>` : ''}
          `;
        }
      },
      
      grid: {
        left: '10%',
        right: '10%',
        bottom: '20%',
        top: '15%',
        containLabel: true
      },
      
      xAxis: {
        type: 'time',
        boundaryGap: false,
        axisLabel: {
          rotate: 45,
          formatter: (value) => {
            try {
              return formatDateInTimezone(value, userTimezone, 'MM/dd HH:mm');
            } catch (error) {
              return new Date(value).toLocaleDateString();
            }
          }
        }
      },
      
      yAxis: {
        type: 'value',
        scale: true,
        name: 'Volume',
        nameLocation: 'middle',
        nameGap: 50,
        min: yAxisMin,
        max: yAxisMax
      },
      
      series: [
        // Main data series (all points)
        {
          name: 'Volume Data',
          type: 'line',
          data: chartData.map(point => [point.timestamp, point.value]),
          smooth: false,
          symbol: 'circle',
          symbolSize: chartData.length > 100 ? 2 : 4, // Smaller symbols for dense data
          lineStyle: {
            width: chartData.length > 100 ? 0.5 : 1, // Thinner line for dense data
            color: theme.palette.waltero.primary.mediumBlue
          },
          itemStyle: {
            color: theme.palette.waltero.primary.mediumBlue
          },
          emphasis: {
            itemStyle: {
              borderWidth: 3,
              borderColor: theme.palette.waltero.primary.coreBlue,
              shadowBlur: 8,
              shadowColor: 'rgba(0,0,0,0.3)'
            },
            scale: 1.5 // Make points bigger on hover to indicate they're clickable
          },
          showSymbol: chartData.length <= 500, // Hide symbols if too many points
          sampling: chartData.length > 1000 ? 'lttb' : null, // Use sampling for very dense data
          z: 1, // Lower z-index so selected points appear on top
          cursor: 'pointer' // Show pointer cursor to indicate clickable
        },
        // Selected points series (highlighted in red)
        {
          name: 'Selected Points',
          type: 'scatter',
          data: sortedSelectedPoints.map(point => [point.timestamp, point.value]),
          symbol: 'circle',
          symbolSize: 8, // Larger symbols for selected points
          itemStyle: {
            color: theme.palette.waltero.status.error, // Bright red color
            borderWidth: 2,
            borderColor: theme.palette.common.white // White border for contrast
          },
          emphasis: {
            itemStyle: {
              color: theme.palette.waltero.status.error, // Even brighter red on hover
              borderWidth: 3,
              borderColor: theme.palette.common.white
            }
          },
          z: 2 // Higher z-index to appear on top
        },
        // Preview series (showing modified values in green)
        {
          name: 'Modified Values',
          type: 'scatter',
          data: previewChanges.map(change => [change.timestamp, change.newValue]),
          symbol: 'diamond',
          symbolSize: 10, // Slightly larger symbols for preview
          itemStyle: {
            color: theme.palette.waltero.status.success, // Bright green color
            borderWidth: 2,
            borderColor: theme.palette.common.white // White border for contrast
          },
          emphasis: {
            itemStyle: {
              color: theme.palette.waltero.status.success, // Darker green on hover
              borderWidth: 3,
              borderColor: theme.palette.common.white
            }
          },
          z: 3 // Highest z-index to appear on top of everything
        },
        // Filtered data series (showing corrected values in purple) - Admin only
        ...(isAdmin && showFilteredData && filteredData?.chartData ? [{
          name: 'Filtered Data',
          type: 'line',
          data: filteredData.chartData.map(point => [point.timestamp, point.value]),
          smooth: false,
          symbol: 'triangle',
          symbolSize: 5,
          lineStyle: {
            width: 2,
            color: theme.palette.waltero.primary.mediumBlue, // Purple color for filtered data
            type: 'dashed' // Dashed line to distinguish from original
          },
          itemStyle: {
            color: theme.palette.waltero.primary.mediumBlue
          },
          emphasis: {
            itemStyle: {
              borderWidth: 3,
              borderColor: theme.palette.waltero.primary.mediumBlue,
              shadowBlur: 8,
              shadowColor: 'rgba(0,0,0,0.3)'
            }
          },
          showSymbol: filteredData.chartData.length <= 500,
          z: 1.5 // Between main data and selected points
        }] : []),
        // Corrected points from filter (highlight actual corrections in bright purple) - Admin only
        ...(isAdmin && showFilteredData && filteredData?.chartData ? [{
          name: 'Filter Corrections',
          type: 'scatter',
          data: filteredData.chartData.filter(point => point.corrected).map(point => [point.timestamp, point.value]),
          symbol: 'diamond',
          symbolSize: 8,
          itemStyle: {
            color: theme.palette.waltero.status.warning, // Bright pink for corrections
            borderWidth: 2,
            borderColor: '#ffffff'
          },
          emphasis: {
            itemStyle: {
              color: theme.palette.waltero.status.warning,
              borderWidth: 3,
              borderColor: theme.palette.common.white
            }
          },
          z: 2.5 // Higher than filtered line, lower than manual selections
        }] : [])
      ],
      
      // Legend to show what different points mean
      legend: {
        show: sortedSelectedPoints.length > 0 || previewChanges.length > 0 || (isAdmin && showFilteredData),
        data: [
          'Volume Data', 
          ...(selectedPoints.length > 0 ? ['Selected Points'] : []),
          ...(previewChanges.length > 0 ? ['Modified Values'] : []),
          ...(isAdmin && showFilteredData && filteredData?.chartData ? ['Filtered Data'] : []),
          ...(isAdmin && showFilteredData && filteredData?.chartData?.some(p => p.corrected) ? ['Filter Corrections'] : [])
        ],
        top: 50,
        right: 20,
        textStyle: {
          fontSize: 12
        }
      },
      
      // Enable brush selection
      brush: {
        toolbox: ['rect'],
        xAxisIndex: 0,
        brushStyle: {
          borderWidth: 2,
          color: 'rgba(61, 141, 174, 0.2)',
          borderColor: theme.palette.waltero.primary.lightBlue
        },
        outOfBrush: {
          colorAlpha: 0.1
        },
        z: 10001,
        throttleType: 'debounce',
        throttleDelay: 300,
        removeOnClick: false // Don't remove brush on click
      },
      
      toolbox: {
        show: true,
        right: 20,
        top: 70,
        feature: {
          brush: {
            type: ['rect'],
            title: {
              rect: 'Select Region'
            }
          },
          restore: {
            title: 'Reset Zoom'
          }
        }
      },
      
      dataZoom: [
        {
          type: 'inside',
          xAxisIndex: 0,
          filterMode: 'none'
        },
        {
          type: 'slider',
          xAxisIndex: 0,
          bottom: '5%',
          height: 25,
          filterMode: 'none'
        }
      ]
    };
  }, [chartData, sortedSelectedPoints, previewChanges, sensor.serialNumber, theme, isAdmin, showFilteredData, filteredData, userTimezone]);

  // Handle brush selection events
  const handleBrushSelected = useCallback((params) => {
    console.log('🎯 =================================');
    console.log('🎯 BRUSH SELECTION EVENT');
    console.log('🎯 =================================');
    console.log('🎯 Full params object:', JSON.stringify(params, null, 2));
    console.log('🎯 Event keys:', Object.keys(params));
    console.log('🎯 Event type:', params.type || 'unknown');
    
    if (params.areas && params.areas.length > 0) {
      const area = params.areas[0];
      console.log('🎯 Raw brush area object:', JSON.stringify(area, null, 2));
      console.log('🎯 Area properties:', Object.keys(area));
      
      // Let's examine ALL possible coordinate sources
      console.log('🔍 COORDINATE ANALYSIS:');
      console.log('  - area.coordRange:', area.coordRange);
      console.log('  - area.range:', area.range);
      console.log('  - area.panelId:', area.panelId);
      console.log('  - area.brushType:', area.brushType);
      
      // Chart data context for reference
      if (chartData.length > 0) {
        const firstTimestamp = new Date(chartData[0].timestamp).getTime();
        const lastTimestamp = new Date(chartData[chartData.length - 1].timestamp).getTime();
        console.log('📊 CHART CONTEXT:');
        console.log('  - Chart data points:', chartData.length);
        console.log('  - First timestamp:', firstTimestamp, '=', new Date(firstTimestamp).toISOString());
        console.log('  - Last timestamp:', lastTimestamp, '=', new Date(lastTimestamp).toISOString());
        console.log('  - Duration:', (lastTimestamp - firstTimestamp) / (1000 * 60), 'minutes');
      }
      
      let xStart, xEnd, yStart, yEnd;
      let coordinateMethod = 'unknown';
      
      // Try coordRange first (most common)
      if (area.coordRange && Array.isArray(area.coordRange) && area.coordRange.length >= 2) {
        console.log('🎯 Trying coordRange method...');
        
        if (Array.isArray(area.coordRange[0])) {
          // 2D format: [[xMin, xMax], [yMin, yMax]] - X axis range is first array, Y axis range is second array
          xStart = area.coordRange[0][0]; // X minimum
          xEnd = area.coordRange[0][1];   // X maximum
          yStart = area.coordRange[1][0]; // Y minimum  
          yEnd = area.coordRange[1][1];   // Y maximum
          coordinateMethod = '2D coordRange [[xMin,xMax], [yMin,yMax]]';
        } else {
          // 1D format: [xMin, xMax] - only X coordinates available
          xStart = area.coordRange[0];
          xEnd = area.coordRange[1];
          yStart = null;
          yEnd = null;
          coordinateMethod = '1D coordRange [xMin, xMax] - no Y filtering';
        }
        
        console.log('🎯 Method:', coordinateMethod);
        console.log('🎯 Raw extracted values:', { xStart, xEnd, yStart, yEnd });
        console.log('🎯 Value types:', { xStartType: typeof xStart, xEndType: typeof xEnd, yStartType: typeof yStart, yEndType: typeof yEnd });
        
        // Detailed coordinate analysis
        console.log('🔍 COORDINATE VALUES:');
        console.log('  - xStart as number:', Number(xStart));
        console.log('  - xEnd as number:', Number(xEnd));
        console.log('  - xStart as date:', new Date(Number(xStart)).toISOString());
        console.log('  - xEnd as date:', new Date(Number(xEnd)).toISOString());
        
        // Check if they look like valid timestamps (within reasonable bounds)
        const currentTime = Date.now();
        const oneYearAgo = currentTime - (365 * 24 * 60 * 60 * 1000);
        const oneDayFromNow = currentTime + (24 * 60 * 60 * 1000);
        
        console.log('🔍 TIMESTAMP VALIDATION:');
        console.log('  - Current time:', currentTime, '=', new Date(currentTime).toISOString());
        console.log('  - One year ago:', oneYearAgo, '=', new Date(oneYearAgo).toISOString());
        console.log('  - One day from now:', oneDayFromNow, '=', new Date(oneDayFromNow).toISOString());
        console.log('  - xStart in valid range:', xStart >= oneYearAgo && xStart <= oneDayFromNow);
        console.log('  - xEnd in valid range:', xEnd >= oneYearAgo && xEnd <= oneDayFromNow);
        
        if (xStart >= oneYearAgo && xStart <= oneDayFromNow && xEnd >= oneYearAgo && xEnd <= oneDayFromNow) {
          console.log('✅ Coordinates look like valid timestamps');
        } else {
          console.log('🚨 Coordinates don\'t look like timestamps - need conversion');
          
          // Maybe they're data indices or normalized values?
          if (chartData.length > 0) {
            const chartStartTime = new Date(chartData[0].timestamp).getTime();
            const chartEndTime = new Date(chartData[chartData.length - 1].timestamp).getTime();
            const chartDuration = chartEndTime - chartStartTime;
            
            console.log('🔧 ATTEMPTING COORDINATE CONVERSION:');
            
            if (xStart >= 0 && xStart <= chartData.length && xEnd >= 0 && xEnd <= chartData.length) {
              // Treat as data indices
              const startIndex = Math.floor(Math.min(xStart, xEnd));
              const endIndex = Math.floor(Math.max(xStart, xEnd));
              
              console.log('  - Treating as data indices:', { startIndex, endIndex });
              
              if (startIndex >= 0 && endIndex < chartData.length) {
                xStart = new Date(chartData[startIndex].timestamp).getTime();
                xEnd = new Date(chartData[Math.min(endIndex, chartData.length - 1)].timestamp).getTime();
                console.log('  - Converted to timestamps:', { xStart, xEnd });
                coordinateMethod += ' -> converted from data indices';
              }
            } else if (xStart >= 0 && xStart <= 1 && xEnd >= 0 && xEnd <= 1) {
              // Treat as normalized coordinates [0,1]
              console.log('  - Treating as normalized coordinates [0,1]');
              xStart = chartStartTime + (xStart * chartDuration);
              xEnd = chartStartTime + (xEnd * chartDuration);
              console.log('  - Converted to timestamps:', { xStart, xEnd });
              coordinateMethod += ' -> converted from normalized [0,1]';
            } else {
              console.log('  - Unable to convert coordinates, using as-is');
            }
          }
        }
        
        // Ensure proper order (left to right, bottom to top)
        const startTime = Math.min(xStart, xEnd);
        const endTime = Math.max(xStart, xEnd);
        const minValue = yStart !== null && yEnd !== null ? Math.min(yStart, yEnd) : null;
        const maxValue = yStart !== null && yEnd !== null ? Math.max(yStart, yEnd) : null;
        
        console.log('🎯 FINAL SELECTION RANGE:');
        console.log('  - Start time:', startTime, '=', new Date(startTime).toISOString());
        console.log('  - End time:', endTime, '=', new Date(endTime).toISOString());
        console.log('  - Duration:', (endTime - startTime) / (1000 * 60), 'minutes');
        if (minValue !== null && maxValue !== null) {
          console.log('  - Min value:', minValue);
          console.log('  - Max value:', maxValue);
          console.log('  - Value range:', maxValue - minValue);
        } else {
          console.log('  - Y filtering: DISABLED (no Y coordinates available)');
        }
        console.log('  - Method used:', coordinateMethod);
        
        // Find matching data points - filter by both X (time) and Y (value) coordinates
        const matchingPoints = [];
        chartData.forEach((point, index) => {
          const timestamp = new Date(point.timestamp).getTime();
          const value = point.value;
          
          // Check X-axis (timestamp) constraint
          const isInTimeRange = timestamp >= startTime && timestamp <= endTime;
          
          // Check Y-axis (value) constraint if Y coordinates are available
          let isInValueRange = true;
          if (minValue !== null && maxValue !== null) {
            isInValueRange = value >= minValue && value <= maxValue;
          }
          
          // Point must be in both X and Y range to be selected
          const isSelected = isInTimeRange && isInValueRange;
          
          if (index < 5 || isSelected) { // Log first 5 points and all matching points
            console.log(`  - Point ${index}: ${timestamp} (${new Date(timestamp).toISOString()}) = ${value} - Time: ${isInTimeRange ? '✅' : '❌'}, Value: ${isInValueRange ? '✅' : '❌'} → ${isSelected ? '✅ SELECTED' : '❌'}`);
          }
          
          if (isSelected) {
            matchingPoints.push({
              index: index,
              ...point
            });
          }
        });
        
        console.log('🎯 SELECTION RESULT:');
        console.log('  - Points found:', matchingPoints.length);
        if (matchingPoints.length > 0) {
          console.log('  - First selected:', new Date(matchingPoints[0].timestamp).toISOString(), '=', matchingPoints[0].value);
          console.log('  - Last selected:', new Date(matchingPoints[matchingPoints.length - 1].timestamp).toISOString(), '=', matchingPoints[matchingPoints.length - 1].value);
        }
        
        if (matchingPoints.length > 0) {
          // Merge with existing selection instead of replacing
          console.log('🔄 STARTING MERGE PROCESS...');
          console.log('  - Current selectedPoints state:', selectedPoints?.length || 0, 'points');
          console.log('  - selectedPoints array:', selectedPoints?.slice(0, 3).map(p => ({ timestamp: p.timestamp, value: p.value })));
          
          const existingPoints = selectedPoints || [];
          const mergedPoints = [...existingPoints];
          let addedCount = 0;
          let duplicateCount = 0;
          
          console.log('  - Starting with existing points:', existingPoints.length);
          console.log('  - Processing new points:', matchingPoints.length);
          
          // Add new points that aren't already selected
          matchingPoints.forEach((newPoint, index) => {
            const isDuplicate = existingPoints.some(existing => 
              existing.timestamp === newPoint.timestamp && existing.value === newPoint.value
            );
            
            console.log(`    Point ${index}: ${new Date(newPoint.timestamp).toISOString()} = ${newPoint.value} ${isDuplicate ? '(DUPLICATE)' : '(NEW)'}`);
            
            if (!isDuplicate) {
              mergedPoints.push(newPoint);
              addedCount++;
            } else {
              duplicateCount++;
            }
          });
          
          console.log('🔄 MERGE RESULTS:');
          console.log('  - Previous selection:', existingPoints.length, 'points');
          console.log('  - New rectangle found:', matchingPoints.length, 'points');
          console.log('  - Duplicates skipped:', duplicateCount, 'points');
          console.log('  - Actually new points:', addedCount, 'points');
          console.log('  - Total after merge:', mergedPoints.length, 'points');
          
          console.log('🔄 CALLING setSelectedPoints with:', mergedPoints.length, 'points');
          setSelectedPoints(mergedPoints);
          
          // Only clear modifications if this is a new selection (no previous points)
          if (existingPoints.length === 0) {
            console.log('  - Clearing modifications (first selection)');
            setSelectedDigitPosition(null);
            setNewDigitValue('');
            setPreviewChanges([]);
            setIsManualEntryMode(false);
            setManualValue('');
          } else {
            console.log('  - Preserving existing modifications (additive selection)');
          }
          
          console.log('✅ Successfully merged selections - should now have:', mergedPoints.length, 'total points');
        } else {
          console.log('⚠️ No data points found in selected time range');
        }
        
      } else {
        console.log('🚨 No valid coordRange found in brush area');
        console.log('🚨 Available area properties:', Object.keys(area));
      }
      
    } else {
      console.log('⚠️ No brush areas found in selection event');
    }
    
    console.log('🎯 =================================');
  }, [chartData, selectedPoints]);

  // Clear selection
  const clearSelection = () => {
    console.log('🧹 Clearing selection - removing', selectedPoints.length, 'selected points');
    setSelectedPoints([]);
    setSelectedDigitPosition(null);
    setNewDigitValue('');
    setPreviewChanges([]);
    setAdditionalLeftDigits(0);
    setAdditionalRightDigits(0);
    setIsManualEntryMode(false);
    setManualValue('');
  };

  // Add digits to the left (higher significance)
  const addLeftDigit = () => {
    setAdditionalLeftDigits(prev => prev + 1);
    console.log('➕ Added left digit, total additional left digits:', additionalLeftDigits + 1);
  };

  // Add digits to the right (lower significance) 
  const addRightDigit = () => {
    setAdditionalRightDigits(prev => prev + 1);
    console.log('➕ Added right digit, total additional right digits:', additionalRightDigits + 1);
  };

  // Remove digits from the left
  const removeLeftDigit = () => {
    if (additionalLeftDigits > 0) {
      setAdditionalLeftDigits(prev => prev - 1);
      console.log('➖ Removed left digit, remaining additional left digits:', additionalLeftDigits - 1);
    }
  };

  // Remove digits from the right
  const removeRightDigit = () => {
    if (additionalRightDigits > 0) {
      setAdditionalRightDigits(prev => prev - 1);
      console.log('➖ Removed right digit, remaining additional right digits:', additionalRightDigits - 1);
    }
  };

  // Remove individual point from selection
  const removePointFromSelection = useCallback((pointIndex) => {
    console.log('🗑️ Removing point', pointIndex, 'from selection');
    const newSelectedPoints = selectedPoints.filter((_, index) => index !== pointIndex);
    setSelectedPoints(newSelectedPoints);
    
    // Clear digit modification if no points remain
    if (newSelectedPoints.length === 0) {
      setSelectedDigitPosition(null);
      setNewDigitValue('');
      setPreviewChanges([]);
      setIsManualEntryMode(false);
      setManualValue('');
    }
  }, [selectedPoints]);

  // Handle individual point clicks
  const handlePointClick = useCallback((params) => {
    console.log('🖱️ Point click event:', params);
    
    // Only handle clicks on the main data series (not selected or preview points)
    if (params.seriesName !== 'Volume Data') {
      console.log('🖱️ Ignoring click on', params.seriesName, 'series');
      return;
    }
    
    // Add a small delay to avoid interference with brush operations
    setTimeout(() => {
      const clickedIndex = params.dataIndex;
      if (clickedIndex < 0 || clickedIndex >= chartData.length) {
        console.log('🖱️ Invalid data index:', clickedIndex);
        return;
      }
      
      const clickedPoint = {
        index: clickedIndex,
        ...chartData[clickedIndex]
      };
      
      console.log('🖱️ Clicked point:', clickedPoint.value, 'at', clickedPoint.displayDateTime);
      
      // Check if point is already selected
      const existingIndex = selectedPoints.findIndex(p => 
        p.timestamp === clickedPoint.timestamp && p.value === clickedPoint.value
      );
      
      if (existingIndex >= 0) {
        // Point is already selected - remove it
        console.log('🖱️ Removing already selected point');
        removePointFromSelection(existingIndex);
      } else {
        // Point is not selected - add it
        console.log('🖱️ Adding new point to selection');
        const newSelectedPoints = [...selectedPoints, clickedPoint];
        setSelectedPoints(newSelectedPoints);
        
        // Clear digit modification when adding new points (to avoid confusion)
        setSelectedDigitPosition(null);
        setNewDigitValue('');
        setPreviewChanges([]);
        setAdditionalLeftDigits(0);
        setAdditionalRightDigits(0);
        setIsManualEntryMode(false);
        setManualValue('');
      }
    }, 100); // Small delay to let brush events complete first
  }, [chartData, selectedPoints, removePointFromSelection]);

  // Analyze selected values for digit positions
  const availableDigitPositions = useMemo(() => {
    if (selectedPoints.length === 0) return [];
    
    // Find the value with the most digits to ensure we cover all possible positions
    let maxIntegerLength = 0;
    let maxDecimalLength = 0;
    let sampleValue = selectedPoints[0].value;
    
    selectedPoints.forEach(point => {
      const valueStr = point.value.toString();
      const [integerPart, decimalPart = ''] = valueStr.split('.');
      maxIntegerLength = Math.max(maxIntegerLength, integerPart.length);
      maxDecimalLength = Math.max(maxDecimalLength, decimalPart.length);
    });
    
    // Add additional digits from user expansion
    const totalIntegerLength = maxIntegerLength + additionalLeftDigits;
    const totalDecimalLength = maxDecimalLength + additionalRightDigits;
    
    console.log(`🔍 Digit analysis: original integer: ${maxIntegerLength}, decimal: ${maxDecimalLength}, with expansion: ${totalIntegerLength} integer, ${totalDecimalLength} decimal`);
    
    const valueStr = sampleValue.toString();
    const [integerPart, decimalPart = ''] = valueStr.split('.');
    const positions = [];
    
    // Use the total integer length (including additional digits)
    for (let i = 0; i < totalIntegerLength; i++) {
      const position = totalIntegerLength - 1 - i;
      const place = Math.pow(10, position);
      const placeName = position === 0 ? 'Ones' : 
                       position === 1 ? 'Tens' : 
                       position === 2 ? 'Hundreds' : 
                       position === 3 ? 'Thousands' : 
                       position === 4 ? 'Ten Thousands' :
                       position === 5 ? 'Hundred Thousands' :
                       position === 6 ? 'Millions' :
                       `10^${position}`;
      
      // Show the digit from sample value if it exists at this position, otherwise '0' for additional digits
      let sampleDigit = '0';
      if (i >= additionalLeftDigits) {
        const originalIndex = i - additionalLeftDigits;
        if (originalIndex < integerPart.length) {
          sampleDigit = integerPart[originalIndex];
        }
      }
      
      positions.push({
        digitIndex: i,
        place: place,
        placeName: placeName,
        isDecimal: false,
        digit: sampleDigit,
        maxLength: maxIntegerLength
      });
    }
    
    // Use the total decimal length (including additional digits)
    for (let i = 0; i < totalDecimalLength; i++) {
      const place = Math.pow(10, -(i + 1));
      const placeName = i === 0 ? 'Tenths' : 
                       i === 1 ? 'Hundredths' : 
                       i === 2 ? 'Thousandths' : 
                       i === 3 ? 'Ten-Thousandths' :
                       i === 4 ? 'Hundred-Thousandths' :
                       i === 5 ? 'Millionths' :
                       `10^-${i + 1}`;
      
      // Show the digit from sample value if it exists at this position, otherwise '0' for additional digits
      let sampleDigit = '0';
      if (i < decimalPart.length) {
        sampleDigit = decimalPart[i];
      }
      
      positions.push({
        digitIndex: totalIntegerLength + 1 + i,
        place: place,
        placeName: placeName,
        isDecimal: true,
        digit: sampleDigit,
        maxLength: totalDecimalLength
      });
    }
    
    return positions;
  }, [selectedPoints, additionalLeftDigits, additionalRightDigits]);

  // Generate preview of changes
  useEffect(() => {
    if (selectedPoints.length > 0) {
      let changes = [];
      
      if (isManualEntryMode && manualValue !== '') {
        // Manual entry mode - apply the complete value to all selected points
        const parsedValue = parseFloat(manualValue);
        if (!isNaN(parsedValue)) {
          changes = selectedPoints.map((point, pointIndex) => {
            const oldValue = point.value;
            
            console.log(`🔍 Manual entry for point ${pointIndex}: ${oldValue} → ${parsedValue}`);
            
            // Use same timezone logic as digit modification
            const formattedDateTime = formatDateInTimezone(point.timestamp, userTimezone);
            const [formattedDate, formattedTime] = formattedDateTime.split(' ');
            
            return {
              timestamp: point.timestamp,
              measurementTime: point.measurementTime || point.timestamp,
              transmissionTime: point.transmissionTime || point.timestamp,
              oldValue: oldValue,
              newValue: parsedValue,
              date: formattedDate,
              time: formattedTime
            };
          });
          
          console.log(`🔍 Generated ${changes.length} manual entry changes`);
        } else {
          console.log('🚨 Invalid manual value, no preview changes generated');
        }
      } else if (selectedDigitPosition !== null && newDigitValue !== '') {
        // Digit modification mode - use existing logic
        changes = selectedPoints.map((point, pointIndex) => {
        const oldValue = point.value;
        const valueStr = oldValue.toString();
        const [integerPart, decimalPart = ''] = valueStr.split('.');
        
        let newValueStr;
        const pos = availableDigitPositions[selectedDigitPosition];
        
        console.log(`🔍 Processing point ${pointIndex}: ${oldValue}, digit position: ${selectedDigitPosition}, new digit: ${newDigitValue}`);
        
        if (pos.isDecimal) {
          // Decimal modification
          const decimalIndex = pos.digitIndex - pos.maxLength - 1;
          console.log(`🔍 Decimal modification: decimalIndex=${decimalIndex}, digitIndex=${pos.digitIndex}, maxLength=${pos.maxLength}`);
          
          // Pad decimal part if needed
          let paddedDecimalPart = decimalPart.padEnd(Math.max(decimalPart.length, decimalIndex + 1), '0');
          const newDecimalPart = paddedDecimalPart.split('');
          
          if (decimalIndex >= 0 && decimalIndex < newDecimalPart.length) {
            newDecimalPart[decimalIndex] = newDigitValue;
            newValueStr = integerPart + '.' + newDecimalPart.join('');
          } else {
            console.warn(`🚨 Invalid decimal position for value ${oldValue}`);
            return null; // Skip invalid modifications
          }
        } else {
          // Integer modification
          const integerPosition = pos.place; // This is 10^position (e.g., 1, 10, 100, 1000)
          const digitPosition = Math.log10(integerPosition); // Convert back to position (0, 1, 2, 3)
          
          console.log(`🔍 Integer modification: place=${integerPosition}, position=${digitPosition}, integerPart=${integerPart}`);
          
          // Pad integer part if needed (e.g., if trying to modify hundreds place of a 2-digit number)
          let paddedIntegerPart = integerPart.padStart(Math.max(integerPart.length, digitPosition + 1), '0');
          const newIntegerPart = paddedIntegerPart.split('');
          
          // Modify from the right (ones place = index length-1, tens = length-2, etc.)
          const modifyIndex = newIntegerPart.length - 1 - digitPosition;
          
          if (modifyIndex >= 0 && modifyIndex < newIntegerPart.length) {
            newIntegerPart[modifyIndex] = newDigitValue;
            newValueStr = newIntegerPart.join('') + (decimalPart ? '.' + decimalPart : '');
          } else {
            console.warn(`🚨 Invalid integer position for value ${oldValue}`);
            return null; // Skip invalid modifications
          }
        }
        
        const newValue = parseFloat(newValueStr);
        
        console.log(`🔍 Value transformation: ${oldValue} → ${newValue} (${newValueStr})`);
        
        return {
          ...point,
          oldValue: oldValue,
          newValue: newValue,
          change: newValue - oldValue
        };
        }).filter(change => change !== null); // Remove invalid changes
        
        console.log(`🔍 Generated ${changes.length} valid digit changes from ${selectedPoints.length} selected points`);
      }
      
      setPreviewChanges(changes);
    } else {
      setPreviewChanges([]);
    }
  }, [selectedPoints, selectedDigitPosition, newDigitValue, availableDigitPositions, isManualEntryMode, manualValue, userTimezone]);

  // Apply filter chain sequentially
  const applyFilterChain = async () => {
    if (selectedFilterChain.length === 0) {
      setFilterError('No filters selected. Please drag filters to the chain and try again.');
      return;
    }
    
    try {
      setFilterLoading(true);
      setFilterError(null);
      
      console.log('🔄 Applying filter chain:', selectedFilterChain.map(f => f.name));
      
      // Start with original sensor data
      // Helper function: Convert compact format back to aggregated format
      const convertCompactToAggregated = (compactData) => {
        if (!Array.isArray(compactData)) {
          console.warn('⚠️ convertCompactToAggregated: input is not an array, returning as-is');
          return compactData;
        }

        // Check if this is already aggregated format (not compact)
        const firstRecord = compactData[0];
        if (firstRecord && !firstRecord.original && !firstRecord.corrected && 
            firstRecord.volume !== undefined && firstRecord.status !== undefined) {
          console.log('📊 Data is already in aggregated format, no conversion needed');
          return compactData;
        }

        console.log(`🔄 Converting ${compactData.length} compact records to aggregated format`);
        
        return compactData.map(record => {
          if (!record.corrected || !record.original) {
            console.warn('⚠️ Invalid compact record, keeping as-is:', record);
            return record;
          }

          // Use corrected values as the new baseline (this is the key for proper chaining)
          return {
            timestamp: record.timestamp,
            device_id: record.device_id,
            volume: record.corrected.volume !== undefined ? record.corrected.volume : record.original.volume,
            digits: record.corrected.digits !== undefined ? record.corrected.digits : record.original.digits,
            scores: record.corrected.scores !== undefined ? record.corrected.scores : record.original.scores,
            status: record.corrected.status !== undefined ? record.corrected.status : record.original.status,
            // Store original values for final comparison
            _originalVolume: record.original.volume,
            _originalDigits: record.original.digits,
            _originalScores: record.original.scores,
            _originalStatus: record.original.status
          };
        });
      };

      const initialData = await telemetryService.getFilterVisualizationData(
        sensor.deviceId,
        startDate,
        endDate
      );
      
      if (!initialData.data || initialData.data.length === 0) {
        throw new Error('No volume, digits, scores, and status data available for this time period');
      }
      
      let currentData = initialData.data;
      let chainResults = [];
      
      // Apply each filter in sequence, ensuring consistent input format
      for (let i = 0; i < selectedFilterChain.length; i++) {
        const filter = selectedFilterChain[i];
        console.log(`🔄 Applying filter ${i + 1}/${selectedFilterChain.length}: ${filter.name}`);
        
        // CRITICAL: Ensure consistent input format for all filters
        let inputForThisFilter;
        
        if (i === 0) {
          // First filter gets the original aggregated data
          inputForThisFilter = initialData.data;
          console.log(`📊 Filter ${i + 1} input: Original aggregated format (${inputForThisFilter.length} records)`);
        } else {
          // Subsequent filters: convert previous filter's compact output back to aggregated format
          inputForThisFilter = convertCompactToAggregated(currentData);
          console.log(`📊 Filter ${i + 1} input: Converted from compact to aggregated format (${inputForThisFilter.length} records)`);
          
          // Debug: show sample input format
          if (inputForThisFilter.length > 0) {
            console.log(`📊 Sample converted input:`, {
              keys: Object.keys(inputForThisFilter[0]),
              volume: inputForThisFilter[0].volume,
              hasOriginal: !!inputForThisFilter[0]._originalVolume
            });
          }
        }
        
        let filterResult;
        
        // Apply the specific filter based on its type
        switch (filter.id) {
          case 'digitConfusion':
            filterResult = await telemetryService.applyDigitConfusionFilter(inputForThisFilter, filter.params);
            break;
            
          case 'digitCompletion':
            filterResult = await telemetryService.applyDigitCompletionFilter(inputForThisFilter, filter.params);
            break;
            
          default:
            console.warn(`⚠️ Unknown filter type: ${filter.id}`);
            filterResult = { data: inputForThisFilter, corrections: 0 };
        }
        
        // DEBUG: What does the filterResult actually contain?
        console.log('🔍 RAW FILTER RESULT:', {
          filterName: filter.name,
          resultKeys: Object.keys(filterResult),
          corrections: filterResult.corrections,
          metadataCorrections: filterResult.metadata?.corrections,
          dataLength: filterResult.outputData?.length || filterResult.data?.length || 'unknown',
          sampleData: filterResult.outputData?.[0] || filterResult.data?.[0],
          hasOutputData: !!filterResult.outputData,
          hasData: !!filterResult.data,
          outputDataLength: filterResult.outputData?.length,
          dataLength: filterResult.data?.length
        });
        
        // Update currentData for next filter - handle empty outputData case
        if (filterResult.outputData && filterResult.outputData.length > 0) {
          currentData = filterResult.outputData;
          console.log(`🔄 Using outputData for next filter: ${currentData.length} records`);
        } else if (filterResult.data && filterResult.data.length > 0) {
          currentData = filterResult.data;
          console.log(`🔄 Using data for next filter: ${currentData.length} records`);
        } else if (Array.isArray(filterResult.outputData) && filterResult.outputData.length === 0) {
          // Filter returned empty outputData - this means no corrections were made
          // Use the input to this filter for the next filter
          currentData = inputForThisFilter;
          console.log(`⚠️ Filter ${filter.name} returned empty outputData (no corrections made)`);
          console.log(`🔄 Using input data for next filter: ${currentData.length} records`);
        } else {
          console.error(`❌ No valid data found in filter result! Keys: ${Object.keys(filterResult)}`);
          console.error(`❌ filterResult.outputData:`, filterResult.outputData);
          console.error(`❌ filterResult.data:`, filterResult.data);
          throw new Error(`Filter ${filter.name} returned no usable data for chaining`);
        }
        
        const correctionsCount = filterResult.corrections || filterResult.metadata?.corrections || 0;
        
        chainResults.push({
          filterName: filter.name,
          corrections: correctionsCount,
          data: [...currentData]
        });
        
        console.log(`✅ Filter ${filter.name} applied: ${correctionsCount} corrections`);
      }
      
      // Unpack final results
      const finalFilterResult = {
        outputData: currentData,
        corrections: chainResults.reduce((total, result) => total + result.corrections, 0),
        chainResults: chainResults,
        metadata: {
          corrections: chainResults.reduce((total, result) => total + result.corrections, 0),
          totalInputRecords: initialData.data.length,
          processedFilters: chainResults.length
        }
      };
      
      // For filter chains, we need to create chart-compatible data manually
      // since the standard unpack function expects a specific backend format
      let chartData = [];
      if (currentData && Array.isArray(currentData)) {
        console.log('🔍 Processing filter chain output data:', {
          totalRecords: currentData.length,
          sampleRecord: currentData[0],
          recordKeys: currentData[0] ? Object.keys(currentData[0]) : []
        });
        
        // Handle different possible data formats from the filter chain
        chartData = currentData.map(record => {
          let finalValue, originalValue, wasCorrected;
          
          // Handle compact format from backend (most common from digit confusion filter)
          if (record.corrected && record.original && record.flags) {
            finalValue = record.corrected.volume; // This is the CORRECTED value (e.g., 79)
            originalValue = record.original.volume; // This is the ORIGINAL value (e.g., 78)
            wasCorrected = record.flags.volume?.corrected || false;
            
            console.log('🔍 Compact format record:', {
              timestamp: record.timestamp,
              originalValue: originalValue,
              finalValue: finalValue, // This should be the corrected value
              wasCorrected: wasCorrected,
              difference: wasCorrected ? (finalValue - originalValue) : 0
            });
          }
          // Handle measure format (if data comes from earlier in the pipeline)
          else if (record.measure === 'volume' || record.measure_name === 'volume') {
            finalValue = record.value !== undefined ? record.value : record.measure_value;
            originalValue = record.originalValue !== undefined ? record.originalValue : finalValue;
            wasCorrected = record.corrected || false;
          }
          // Handle simple records with direct volume field
          else if (record.volume !== undefined) {
            finalValue = record.volume;
            originalValue = record.originalValue || finalValue;
            wasCorrected = record.corrected || false;
          }
          // Fallback to any value field
          else {
            finalValue = record.value || record.measure_value;
            originalValue = record.originalValue || finalValue;
            wasCorrected = record.corrected || false;
          }
          
          const chartPoint = {
            timestamp: record.timestamp,
            value: finalValue, // This should be the CORRECTED value (79)
            corrected: wasCorrected,
            originalValue: originalValue // This should be the ORIGINAL value (78)
          };
          
          // Debug log EVERY point to see what's being used
          if (wasCorrected) {
            console.log('🟢 CORRECTED point for chart:', {
              timestamp: chartPoint.timestamp,
              originalValue: chartPoint.originalValue,
              correctedValue: chartPoint.value,
              change: chartPoint.value - chartPoint.originalValue
            });
          }
          
          return chartPoint;
        }).filter(item => item.value !== undefined && item.value !== null);
        
        console.log('📊 Created chart data for filter chain:', {
          originalRecords: currentData.length,
          chartPoints: chartData.length,
          correctedPoints: chartData.filter(p => p.corrected).length,
          sampleData: chartData.slice(0, 3).map(p => ({
            timestamp: p.timestamp,
            value: p.value,
            originalValue: p.originalValue,
            corrected: p.corrected,
            change: p.corrected ? (p.value - p.originalValue).toFixed(3) : '0'
          }))
        });
      }
      
      const filteredDataObject = {
        original: initialData,
        filtered: finalFilterResult,
        chartData: chartData,
        metadata: finalFilterResult.metadata,
        chainResults: chainResults
      };
      
      // FINAL DEBUG: What's going into the chart?
      console.log('🎯 FINAL CHART DATA BEING SET:', {
        totalPoints: chartData.length,
        correctedPoints: chartData.filter(p => p.corrected).length,
        firstCorrectedPoint: chartData.find(p => p.corrected),
        sampleValues: chartData.slice(0, 5).map(p => ({ 
          time: p.timestamp, 
          value: p.value, 
          corrected: p.corrected 
        }))
      });
      
      setFilteredData(filteredDataObject);
      setShowFilteredData(true);
      
      console.log('✅ Filter chain completed:', {
        totalCorrections: finalFilterResult.corrections,
        chainLength: selectedFilterChain.length,
        finalDataPoints: chartData.length
      });
      
    } catch (err) {
      console.error('❌ Failed to apply filter chain:', err);
      
      if (err.response?.status === 403) {
        setFilterError('Admin access required for filter chain functionality');
      } else if (err.response?.status === 404) {
        setFilterError('Device not found or access denied');
      } else {
        setFilterError(err.message || 'Failed to apply filter chain');
      }
    } finally {
      setFilterLoading(false);
    }
  };
  
  // Load filter visualization data (single filter - kept for backward compatibility)
  const loadFilterData = async () => {
    try {
      setFilterLoading(true);
      setFilterError(null);
      
      // Fetch volume, digits, scores, status data with optimized two-call approach
      // This ensures volume data uses proper table logic (historical + current)
      // while OCR data comes from InternalFormatData only
      const filterInputData = await telemetryService.getFilterVisualizationData(
        sensor.deviceId,
        startDate,
        endDate
      );
      
      if (!filterInputData.data || filterInputData.data.length === 0) {
        throw new Error('No volume, digits, scores, and status data available for this time period');
      }
      
      
      // Apply the digit confusion filter
      const filterResult = await telemetryService.applyDigitConfusionFilter(
        filterInputData.data,
        {
          confidenceThreshold: 0.7,
          windowSize: 7,
          statisticalThreshold: 2.0,
          positionWeighting: true
        }
      );
      
      // Unpack filter results using the new compact format support
      const unpackedResults = telemetryService.unpackFilterResults(filterResult);
      
      const filteredDataObject = {
        original: filterInputData,
        filtered: filterResult,
        chartData: unpackedResults.filteredData,
        metadata: unpackedResults.metadata
      };
      
      
      setFilteredData(filteredDataObject);
      setShowFilteredData(true);
      
      
    } catch (err) {
      console.error('❌ Failed to load filter data:', err);
      
      // Enhanced error handling for admin access
      if (err.response?.status === 403) {
        setFilterError('Admin access required for filter visualization');
      } else if (err.response?.status === 404) {
        setFilterError('Device not found or access denied');
      } else {
        setFilterError(err.message || 'Failed to load filter visualization data');
      }
    } finally {
      setFilterLoading(false);
    }
  };

  // Transform filtered data for chart display
  const transformFilteredDataForChart = (filteredOutputData) => {
    console.log('📊 Transforming filtered data for chart:', filteredOutputData?.length || 0, 'records');
    
    if (!Array.isArray(filteredOutputData)) {
      console.warn('⚠️ Filtered output data is not an array:', filteredOutputData);
      return [];
    }
    
    const volumeData = filteredOutputData.filter(record => record.measure === 'volume');
    console.log('📊 Volume records found:', volumeData.length);
    
    const transformed = volumeData.map(record => {
      const timestamp = record.timestamp;
      const value = record.corrected ? record.value : (record.originalValue || record.value);
      const originalValue = record.originalValue;
      
      // Format timestamps in user's timezone for display
      const userTimezoneDateTime = formatDateInTimezone(timestamp, userTimezone, 'yyyy-MM-dd HH:mm:ss');
      
      return {
        timestamp: timestamp,
        value: value,
        originalValue: originalValue,
        corrected: record.corrected || false,
        displayDateTime: userTimezoneDateTime
      };
    }).sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
    
    console.log('📊 Transformed filtered data:', transformed.length, 'points, sample:', transformed.slice(0, 3));
    return transformed;
  };

  // Apply changes
  const applyChanges = async () => {
    if (previewChanges.length === 0) return;
    
    try {
      setUpdating(true);
      setUpdateError(null);
      setUpdateSuccess(null);
      
      // Prepare updates for backend API
      const updates = previewChanges.map(change => {
        // Convert measurement timestamp to proper ISO format for backend
        const measurementTime = new Date(change.measurementTime || change.timestamp).toISOString().replace(/\.\d{3}Z$/, 'Z');
        // Use transmission time from the original record metadata
        const transmissionTime = change.transmissionTime || change.timestamp;
        
        console.log('🔍 Update mapping:', {
          measurementTime: measurementTime,
          transmissionTime: transmissionTime,
          originalTimestamp: change.timestamp,
          hasMetadata: !!(change.transmissionTime)
        });
        
        return {
          deviceid: sensor.deviceId,
          timestamp: measurementTime, // Measurement time (when sensor took reading)
          time: transmissionTime, // Transmission time (when data was sent)
          new_meter_value: change.newValue,
          old_meter_value: change.oldValue
        };
      });
      
      console.log('🔄 Applying', updates.length, 'changes...');
      const result = await telemetryService.updateMeasureValues(updates);
      
      if (result.success) {
        setUpdateSuccess(`Successfully updated ${result.successful_updates} of ${result.total_updates} data points`);
        
        // Clear selection and reload chart data immediately
        clearSelection();
        
        // Reload the chart data to reflect changes (stay in modal)
        console.log('🔄 Reloading chart data after successful updates...');
        await loadSensorData();
        
        // Track that data was updated this session (will notify parent on close)
        setDataWasUpdatedThisSession(true);
        
        // Note: Removed parent notification to prevent full report reload
        // Parent will be notified when modal is closed
        
        // Auto-hide success message after 5 seconds
        setTimeout(() => {
          setUpdateSuccess(null);
        }, 5000);
      } else {
        throw new Error(`Update failed: ${result.failed_updates} of ${result.total_updates} updates failed`);
      }
      
    } catch (err) {
      console.error('❌ Failed to apply changes:', err);
      setUpdateError(err.message || 'Failed to apply changes');
    } finally {
      setUpdating(false);
    }
  };

  return (
    <Dialog
      open={true}
      onClose={() => onClose(dataWasUpdatedThisSession)}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: theme.palette.waltero.secondary.backgroundApricot
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          bgcolor: theme.palette.waltero.primary.coreBlue,
          color: 'white'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <EditIcon />
          <WalteroTypography variant="h5">
            Data Correction - {sensor.serialNumber}
          </WalteroTypography>
          <WalteroStatusChip label="Admin Only" size="small" status="info" />
        </Box>
        <IconButton onClick={() => onClose(dataWasUpdatedThisSession)} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 0, height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column' }}>
        {/* Chart Area - 70% */}
        <Box sx={{ flex: '0 0 70%', p: 2, minHeight: '500px' }}>
          <WalteroCard sx={{ height: '100%', display: 'flex', flexDirection: 'column', p: 2 }}>
            {loading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                <CircularProgress size={60} />
                <WalteroTypography sx={{ ml: 2 }}>Loading sensor data...</WalteroTypography>
              </Box>
            )}
            
            {error && (
              <Alert severity="error" sx={{ m: 2 }}>
                {error}
              </Alert>
            )}
            
            {!loading && !error && sensorData && (
              <WalteroChart
                option={chartOption}
                height={500}
                onEvents={{
                  brushEnd: handleBrushSelected, // Final brush selection
                  click: handlePointClick, // Individual point clicks
                  dataZoom: (params) => console.log('🎯 DataZoom event:', params),
                  selectchanged: (params) => console.log('🎯 SelectChanged event:', params)
                }}
                notMerge={false}
                lazyUpdate={true}
                autoResize={true}
              />
            )}
          </WalteroCard>
        </Box>

        {/* Control Panel - 30% */}
        <Box sx={{ flex: '0 0 30%', p: 2, borderTop: `1px solid ${theme.palette.divider}` }}>
          <Grid container spacing={2} sx={{ height: '100%' }}>
            {/* Selection Info */}
            <Grid item xs={12} md={4}>
              <WalteroCard sx={{ height: '100%', p: 2 }}>
                <WalteroTypography variant="h6" gutterBottom>
                  Selection ({selectedPoints.length} points{selectedPoints.length > 0 ? ' - multiple rectangles supported' : ''})
                </WalteroTypography>
                
                {selectedPoints.length === 0 ? (
                  <WalteroTypography color="secondary">
                    Draw rectangles on the chart to select data points. Multiple rectangles will add to your selection.
                  </WalteroTypography>
                ) : (
                  <>
                    {/* Summary Info */}
                    <WalteroTypography variant="body2" gutterBottom>
                      Range: {sortedSelectedPoints[0]?.date} to {sortedSelectedPoints[sortedSelectedPoints.length - 1]?.date}
                    </WalteroTypography>
                    <WalteroTypography variant="body2" gutterBottom>
                      Values: {Math.min(...selectedPoints.map(p => p.value)).toFixed(2)} - {Math.max(...selectedPoints.map(p => p.value)).toFixed(2)}
                      {previewChanges.length > 0 && (
                        <WalteroTypography component="span" sx={{ ml: 1, color: theme.palette.success.main, fontWeight: 'bold' }}>
                          (Preview: {Math.min(...previewChanges.map(p => p.newValue)).toFixed(2)} - {Math.max(...previewChanges.map(p => p.newValue)).toFixed(2)})
                        </WalteroTypography>
                      )}
                    </WalteroTypography>
                    
                    {/* Selected Values List */}
                    <Paper 
                      sx={{ 
                        maxHeight: 180, 
                        overflow: 'auto', 
                        mt: 1, 
                        mb: 2,
                        bgcolor: theme.palette.grey[50],
                        border: `1px solid ${theme.palette.divider}`
                      }}
                    >
                      <List dense sx={{ py: 0 }}>
                        {sortedSelectedPoints.map((point, index) => (
                          <ListItem 
                            key={`${point.timestamp}-${index}`} 
                            sx={{ 
                              py: 0.5, 
                              px: 1,
                              borderBottom: index < selectedPoints.length - 1 ? `1px solid ${theme.palette.divider}` : 'none'
                            }}
                            secondaryAction={
                              <IconButton 
                                edge="end" 
                                aria-label="remove point"
                                size="small"
                                onClick={() => {
                                  // Find the original index in the unsorted array
                                  const originalIndex = selectedPoints.findIndex(p => 
                                    p.timestamp === point.timestamp && p.value === point.value
                                  );
                                  removePointFromSelection(originalIndex);
                                }}
                                sx={{ 
                                  color: theme.palette.error.main,
                                  '&:hover': {
                                    bgcolor: theme.palette.error.light,
                                    color: 'white'
                                  }
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            }
                          >
                            <ListItemText
                              primary={
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
                                  <WalteroTypography variant="body2" component="span" sx={{ fontWeight: 'medium' }}>
                                    {point.value}
                                    {previewChanges.find(p => p.timestamp === point.timestamp) && (
                                      <WalteroTypography component="span" sx={{ ml: 1, color: theme.palette.success.main, fontWeight: 'bold' }}>
                                        → {previewChanges.find(p => p.timestamp === point.timestamp)?.newValue}
                                      </WalteroTypography>
                                    )}
                                  </WalteroTypography>
                                  <WalteroTypography variant="caption" component="span" color="text.secondary">
                                    #{index + 1}
                                  </WalteroTypography>
                                </Box>
                              }
                              secondary={
                                <WalteroTypography variant="caption" color="text.secondary">
                                  {point.displayDateTime || `${point.date} ${point.time}`}
                                </WalteroTypography>
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                    
                    <WalteroButton
                      size="small"
                      variant="outlined"
                      onClick={clearSelection}
                      startIcon={<ClearIcon />}
                      fullWidth
                    >
                      Clear Selection
                    </WalteroButton>
                  </>
                )}
              </WalteroCard>
            </Grid>

            {/* Digit Modification */}
            <Grid item xs={12} md={4}>
              <WalteroCard sx={{ height: '100%', p: 2 }}>
                <WalteroTypography variant="h6" gutterBottom>
                  Digit Modification
                </WalteroTypography>
                
                {selectedPoints.length === 0 ? (
                  <WalteroTypography color="secondary">
                    Select data points to modify digits
                  </WalteroTypography>
                ) : (
                  <>
                    <WalteroTypography variant="body2" gutterBottom>
                      Sample value: {selectedPoints[0]?.value}
                    </WalteroTypography>
                    
                    {/* Mode Toggle - Tab Style */}
                    <Box sx={{ 
                      mb: 2, 
                      display: 'flex', 
                      borderRadius: '10px', 
                      overflow: 'hidden', 
                      border: '2px solid', 
                      borderColor: 'waltero.primary.lightBlue',
                      backgroundColor: 'waltero.white',
                      p: '4px'
                    }}>
                      <Box
                        onClick={() => {
                          if (isManualEntryMode) {
                            setIsManualEntryMode(false);
                            setSelectedDigitPosition(null);
                            setNewDigitValue('');
                            setManualValue('');
                            setPreviewChanges([]);
                          }
                        }}
                        sx={{
                          flex: 1,
                          p: 1.5,
                          textAlign: 'center',
                          cursor: 'pointer',
                          bgcolor: !isManualEntryMode ? 'waltero.secondary.primaryApricot' : 'transparent',
                          color: !isManualEntryMode ? 'waltero.white' : 'waltero.primary.coreBlue',
                          borderRadius: '8px',
                          boxShadow: !isManualEntryMode ? '0px 2px 8px rgba(241, 136, 73, 0.25)' : 'none',
                          '&:hover': {
                            bgcolor: !isManualEntryMode 
                              ? 'waltero.secondary.darkApricot' 
                              : 'waltero.primary.neutralBlue',
                            boxShadow: !isManualEntryMode 
                              ? '0px 4px 12px rgba(195, 93, 44, 0.3)' 
                              : 'none',
                            transform: !isManualEntryMode ? 'translateY(-1px)' : 'none'
                          },
                          transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          '&:active': {
                            transform: 'translateY(0px)'
                          }
                        }}
                      >
                        <WalteroTypography variant="body2" sx={{ 
                          fontWeight: !isManualEntryMode ? 600 : 500,
                          fontSize: '0.9rem',
                          color: 'inherit'
                        }}>
                          Digit Mode
                        </WalteroTypography>
                      </Box>
                      <Box
                        onClick={() => {
                          if (!isManualEntryMode) {
                            setIsManualEntryMode(true);
                            setSelectedDigitPosition(null);
                            setNewDigitValue('');
                            setManualValue('');
                            setPreviewChanges([]);
                          }
                        }}
                        sx={{
                          flex: 1,
                          p: 1.5,
                          textAlign: 'center',
                          cursor: 'pointer',
                          bgcolor: isManualEntryMode ? 'waltero.secondary.primaryApricot' : 'transparent',
                          color: isManualEntryMode ? 'waltero.white' : 'waltero.primary.coreBlue',
                          borderRadius: '8px',
                          boxShadow: isManualEntryMode ? '0px 2px 8px rgba(241, 136, 73, 0.25)' : 'none',
                          '&:hover': {
                            bgcolor: isManualEntryMode 
                              ? 'waltero.secondary.darkApricot' 
                              : 'waltero.primary.neutralBlue',
                            boxShadow: isManualEntryMode 
                              ? '0px 4px 12px rgba(195, 93, 44, 0.3)' 
                              : 'none',
                            transform: isManualEntryMode ? 'translateY(-1px)' : 'none'
                          },
                          transition: 'all 0.25s cubic-bezier(0.25, 0.46, 0.45, 0.94)',
                          '&:active': {
                            transform: 'translateY(0px)'
                          }
                        }}
                      >
                        <WalteroTypography variant="body2" sx={{ 
                          fontWeight: isManualEntryMode ? 600 : 500,
                          fontSize: '0.9rem',
                          color: 'inherit'
                        }}>
                          Manual Entry
                        </WalteroTypography>
                      </Box>
                    </Box>
                    
                    {isManualEntryMode ? (
                      // Manual entry mode UI
                      <Box>
                        <WalteroTypography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          Enter a complete value that will be applied to all {selectedPoints.length} selected points.
                        </WalteroTypography>
                        
                        <TextField
                          label="Complete Value"
                          value={manualValue}
                          onChange={(e) => {
                            const value = e.target.value;
                            // Allow numbers, decimal point, and minus sign
                            if (value === '' || /^-?\d*\.?\d*$/.test(value)) {
                              setManualValue(value);
                            }
                          }}
                          size="small"
                          fullWidth
                          type="number"
                          inputProps={{ 
                            step: 'any',
                            placeholder: 'e.g., 123.45'
                          }}
                          helperText="Enter the exact value to set for all selected points"
                          error={manualValue !== '' && isNaN(parseFloat(manualValue))}
                        />
                        
                        {manualValue !== '' && isNaN(parseFloat(manualValue)) && (
                          <WalteroTypography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                            Please enter a valid number
                          </WalteroTypography>
                        )}
                      </Box>
                    ) : (
                      // Digit modification mode UI
                      <>
                    {/* Digit position selector with expansion controls */}
                    <Box sx={{ mb: 2 }}>
                      {/* Expansion controls */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                        <IconButton
                          size="small"
                          onClick={addLeftDigit}
                          title="Add digit to left (higher significance)"
                          sx={{ color: 'primary.main' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                        <WalteroTypography variant="caption" color="text.secondary">
                          Add Higher
                        </WalteroTypography>
                        
                        <Box sx={{ flexGrow: 1 }} />
                        
                        <WalteroTypography variant="caption" color="text.secondary">
                          Add Lower
                        </WalteroTypography>
                        <IconButton
                          size="small"
                          onClick={addRightDigit}
                          title="Add digit to right (lower significance)"
                          sx={{ color: 'primary.main' }}
                        >
                          <AddIcon fontSize="small" />
                        </IconButton>
                      </Box>
                      
                      {/* Digit chips */}
                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'center' }}>
                        {availableDigitPositions.map((pos, index) => (
                          <Chip
                            key={index}
                            label={`${pos.digit} (${pos.placeName})`}
                            onClick={() => setSelectedDigitPosition(index)}
                            color={selectedDigitPosition === index ? 'primary' : 'default'}
                            variant={selectedDigitPosition === index ? 'filled' : 'outlined'}
                            size="small"
                            sx={{
                              // Highlight additional digits in a different color
                              ...(index < additionalLeftDigits || index >= availableDigitPositions.length - additionalRightDigits ? {
                                bgcolor: selectedDigitPosition === index ? 'success.main' : 'success.light',
                                color: selectedDigitPosition === index ? 'success.contrastText' : 'success.dark',
                                '&:hover': {
                                  bgcolor: 'success.main',
                                  color: 'success.contrastText'
                                }
                              } : {})
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                    
                    {selectedDigitPosition !== null && (
                      <TextField
                        label="New Digit (0-9)"
                        value={newDigitValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (/^[0-9]?$/.test(value)) {
                            setNewDigitValue(value);
                          }
                        }}
                        size="small"
                        fullWidth
                        inputProps={{ maxLength: 1 }}
                      />
                    )}
                      </>
                    )}
                  </>
                )}
              </WalteroCard>
            </Grid>

            {/* Preview & Actions */}
            <Grid item xs={12} md={4}>
              <WalteroCard sx={{ height: '100%', p: 2 }}>
                <WalteroTypography variant="h6" gutterBottom>
                  Preview & Actions
                </WalteroTypography>
                
                {/* Filter Visualization - Available for admins regardless of preview changes */}
                {isAdmin && (
                  <>
                    {/* Toggle Filter Interface */}
                    <WalteroButton
                      variant={showFilterInterface ? "outlined" : "secondary"}
                      onClick={() => {
                        setShowFilterInterface(!showFilterInterface);
                        if (showFilterInterface) {
                          setShowFilteredData(false);
                          setFilteredData(null);
                        }
                      }}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      {showFilterInterface ? 'Hide Filter Interface' : 'Show Filter Interface'}
                    </WalteroButton>
                    
                    {/* Filter Chain Interface */}
                    {showFilterInterface && (
                      <Box sx={{ mb: 2, p: 2, border: '2px dashed', borderColor: 'primary.light', borderRadius: 2 }}>
                        <WalteroTypography variant="subtitle2" gutterBottom>
                          Available Filters (drag to chain below)
                        </WalteroTypography>
                        
                        {/* Available Filters Box */}
                        <Box 
                          sx={{ 
                            mb: 2, 
                            p: 1, 
                            minHeight: '60px',
                            border: '1px solid', 
                            borderColor: 'divider', 
                            borderRadius: 1,
                            bgcolor: 'grey.50',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 1
                          }}
                        >
                          {availableFilters.filter(f => !selectedFilterChain.find(sf => sf.id === f.id)).map(filter => (
                            <Chip
                              key={filter.id}
                              label={filter.name}
                              draggable
                              onDragStart={(e) => {
                                setDraggedFilter(filter);
                                e.dataTransfer.effectAllowed = 'move';
                              }}
                              onDragEnd={() => setDraggedFilter(null)}
                              sx={{ 
                                cursor: 'grab',
                                '&:active': { cursor: 'grabbing' },
                                bgcolor: 'primary.light',
                                color: 'primary.contrastText',
                                '&:hover': { bgcolor: 'primary.main' }
                              }}
                              title={filter.description}
                            />
                          ))}
                          {availableFilters.filter(f => !selectedFilterChain.find(sf => sf.id === f.id)).length === 0 && (
                            <WalteroTypography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                              All filters are in use
                            </WalteroTypography>
                          )}
                        </Box>
                        
                        <WalteroTypography variant="subtitle2" gutterBottom>
                          Filter Chain (multiple filters will be applied in sequence)
                        </WalteroTypography>
                        
                        {/* Filter Chain Drop Zone */}
                        <Box 
                          sx={{ 
                            mb: 2, 
                            p: 2, 
                            minHeight: '80px',
                            border: '2px dashed', 
                            borderColor: selectedFilterChain.length > 0 ? 'success.main' : 'warning.main',
                            borderRadius: 1,
                            bgcolor: selectedFilterChain.length > 0 ? 'success.light' : 'warning.light',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 1
                          }}
                          onDragOver={(e) => {
                            e.preventDefault();
                            e.dataTransfer.dropEffect = 'move';
                          }}
                          onDrop={(e) => {
                            e.preventDefault();
                            if (draggedFilter) {
                              // If dropping from available filters, add to chain
                              if (!selectedFilterChain.find(f => f.id === draggedFilter.id)) {
                                setSelectedFilterChain([...selectedFilterChain, draggedFilter]);
                              }
                              setDraggedFilter(null);
                            }
                          }}
                        >
                          {selectedFilterChain.length === 0 ? (
                            <WalteroTypography variant="caption" color="text.secondary" sx={{ textAlign: 'center', alignSelf: 'center' }}>
                              Drop filters here to build your filter chain
                            </WalteroTypography>
                          ) : (
                            selectedFilterChain.map((filter, index) => (
                              <Box key={`${filter.id}-${index}`} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <WalteroTypography variant="body2" sx={{ fontWeight: 'bold', minWidth: '20px' }}>
                                  {index + 1}.
                                </WalteroTypography>
                                <Chip
                                  label={filter.name}
                                  onDelete={() => {
                                    setSelectedFilterChain(selectedFilterChain.filter((_, i) => i !== index));
                                  }}
                                  sx={{ 
                                    bgcolor: 'success.main',
                                    color: 'success.contrastText',
                                    '& .MuiChip-deleteIcon': {
                                      color: 'success.contrastText'
                                    }
                                  }}
                                  title={`${filter.description} (Click X to remove)`}
                                />
                              </Box>
                            ))
                          )}
                        </Box>
                        
                        {/* Apply Filter Chain Button */}
                        <WalteroButton
                          variant="primary"
                          onClick={applyFilterChain}
                          disabled={filterLoading || selectedFilterChain.length === 0}
                          startIcon={filterLoading ? <CircularProgress size={16} /> : <EditIcon />}
                          fullWidth
                          sx={{ mb: 1 }}
                        >
                          {filterLoading ? 'Applying Filters...' : `Apply ${selectedFilterChain.length} Filter${selectedFilterChain.length !== 1 ? 's' : ''}`}
                        </WalteroButton>
                        
                        {/* Hide Filtered Data Button */}
                        {showFilteredData && (
                          <WalteroButton
                            variant="outlined"
                            onClick={() => {
                              setShowFilteredData(false);
                              setFilteredData(null);
                            }}
                            fullWidth
                          >
                            Hide Filtered Data
                          </WalteroButton>
                        )}
                      </Box>
                    )}
                    
                    <Divider sx={{ my: 2 }} />
                  </>
                )}

                {previewChanges.length === 0 ? (
                  <WalteroTypography color="secondary">
                    Configure value modification to see preview
                  </WalteroTypography>
                ) : (
                  <>
                    <WalteroTypography variant="body2" gutterBottom>
                      {previewChanges.length} changes ready:
                    </WalteroTypography>
                    
                    <Box sx={{ maxHeight: 100, overflow: 'auto', mb: 2 }}>
                      <List dense>
                        {previewChanges.slice(0, 3).map((change, index) => (
                          <ListItem key={index} sx={{ py: 0 }}>
                            <ListItemText
                              primary={`${change.oldValue} → ${change.newValue}`}
                              secondary={`${change.date} ${change.time} (${userTimezone})`}
                            />
                          </ListItem>
                        ))}
                        {previewChanges.length > 3 && (
                          <ListItem sx={{ py: 0 }}>
                            <ListItemText secondary={`... and ${previewChanges.length - 3} more`} />
                          </ListItem>
                        )}
                      </List>
                    </Box>
                    
                    <WalteroButton
                      variant="primary"
                      onClick={applyChanges}
                      disabled={updating}
                      startIcon={updating ? <CircularProgress size={16} /> : <SaveIcon />}
                      fullWidth
                      sx={{ mb: 2 }}
                    >
                      {updating ? 'Applying...' : 'Apply Changes'}
                    </WalteroButton>
                  </>
                )}
                
                {updateError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {updateError}
                  </Alert>
                )}
                
                {updateSuccess && (
                  <Alert severity="success" sx={{ mt: 2 }}>
                    {updateSuccess}
                  </Alert>
                )}
                
                {isAdmin && filterError && (
                  <Alert severity="error" sx={{ mt: 2 }}>
                    {filterError}
                  </Alert>
                )}
                
                {isAdmin && showFilteredData && filteredData?.filtered && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Filter applied: {filteredData.filtered.corrections} corrections made out of {filteredData.original?.metadata?.timestampGroups || 0} readings
                  </Alert>
                )}
              </WalteroCard>
            </Grid>
          </Grid>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default DataCorrectionModal;